import { formatBackendDateTimeCR } from '../../../utils/date';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  creditoClienteService,
  CCCliente,
  CCJugada,
  CCJugadaIn,
  CCMovimiento,
  CCResumen,
  CCSorteo,
  CCTicket,
} from '../services/creditoClienteService';
import { NumberGrid } from '../components/NumberGrid';
import { AmountSelector } from '../components/AmountSelector';
import { BetSummary } from '../components/BetSummary';
import { AddedBetsPanel } from '../components/AddedBetsPanel';
import { SorteoCard } from '../components/SorteoCard';

// ─── Tipos locales ─────────────────────────────────────────
type Pestaña = 'jugar' | 'tickets' | 'saldo';

interface JugadaLocal {
  id: string;
  sorteo_id: string;
  sorteo_nombre: string;
  sorteo_hora_cierre: string | null;
  num1: string;
  monto: number;        // directo (REV1) o monto normal
  montoRev2: number;    // reventado (REV2), 0 para no-reventado
  esReventado: boolean;
}

// Agrupación para display de jugadas (junta REV1+REV2 del mismo número)
interface JugadaDisplay {
  num1: string;
  tipo: 'NORMAL' | 'REVENTADO';
  monto: number;
  montoRev1: number;
  montoRev2: number;
}

// ─── Helpers ──────────────────────────────────────────────

function formatColones(valor: string | number | null | undefined): string {
  const n = Number(valor || 0);
  return `₡${n.toLocaleString('es-CR')}`;
}

function formatHoraCierre(hora: string | null | undefined): string {
  if (!hora) return '—';
  const [h, m] = hora.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hora;
  return new Date(2026, 0, 1, h, m).toLocaleTimeString('es-CR', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function tiempoRestante(hora: string | null | undefined): string | null {
  if (!hora) return null;
  const [h, m, s] = hora.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const ahora = new Date();
  const cierre = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate(), h, m, s || 0, 0);
  const diff = cierre.getTime() - ahora.getTime();
  if (diff <= 0) return 'Cerrado';
  const minutos = Math.floor(diff / 60000);
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  return horas > 0 ? `${horas}h ${mins}m` : `${mins}m`;
}

function sorteoNombre(ticket: CCTicket, sorteos: CCSorteo[]): string {
  return ticket.sorteo_nombre || sorteos.find((s) => s.id === ticket.sorteo_id)?.nombre || ticket.sorteo_id;
}

function sorteoHoraCierre(ticket: CCTicket, sorteos: CCSorteo[]): string | null {
  return ticket.sorteo_hora_cierre || sorteos.find((s) => s.id === ticket.sorteo_id)?.hora_cierre || null;
}

function maxDigitosPorSorteo(sorteo: CCSorteo | undefined): number {
  const tipo = (sorteo?.tipo_id || '').toUpperCase();
  if (tipo === 'NORMAL' || tipo === 'REVENTADO') return 2;
  if (tipo === 'MONAZOS') return 3;
  return 4;
}

function agruparJugadasParaDisplay(jugadas: CCJugada[]): JugadaDisplay[] {
  const result: JugadaDisplay[] = [];
  const revMap = new Map<string, JugadaDisplay>();

  for (const j of jugadas) {
    if (j.num2 === 'REV1' || j.num2 === 'REV2') {
      if (!revMap.has(j.num1)) {
        revMap.set(j.num1, { num1: j.num1, tipo: 'REVENTADO', monto: 0, montoRev1: 0, montoRev2: 0 });
      }
      const entry = revMap.get(j.num1)!;
      if (j.num2 === 'REV1') entry.montoRev1 = Number(j.monto);
      else entry.montoRev2 = Number(j.monto);
      entry.monto = entry.montoRev1 + entry.montoRev2;
    } else {
      result.push({ num1: j.num1, tipo: 'NORMAL', monto: Number(j.monto), montoRev1: 0, montoRev2: 0 });
    }
  }
  for (const entry of revMap.values()) result.push(entry);
  return result;
}

function badgeTipo(tipo: string): string {
  const map: Record<string, string> = {
    CREDITO_OTORGADO: 'bg-blue-900 text-blue-200',
    CARGO_VENTA: 'bg-red-900 text-red-200',
    ABONO_RECIBIDO: 'bg-green-900 text-green-200',
    ABONO_PAGO: 'bg-green-900 text-green-200',
    PREMIO_DESCUENTO: 'bg-amber-900 text-amber-200',
    AJUSTE_POSITIVO: 'bg-teal-900 text-teal-200',
    AJUSTE_NEGATIVO: 'bg-orange-900 text-orange-200',
    AJUSTE: 'bg-gray-700 text-gray-200',
    ANULACION_CREDITO: 'bg-gray-700 text-gray-200',
    ANULACION_CREDITO_CLIENTE: 'bg-gray-700 text-gray-200',
  };
  return map[tipo] || 'bg-gray-700 text-gray-200';
}

function labelTipo(tipo: string, descripcion?: string | null): string {
  if ((tipo === 'AJUSTE' || tipo === 'ANULACION_CREDITO' || tipo === 'ANULACION_CREDITO_CLIENTE')
      && descripcion?.toLowerCase().includes('anulaci')) {
    return 'Anulación ticket';
  }
  const map: Record<string, string> = {
    CREDITO_OTORGADO: 'Crédito otorgado',
    CARGO_VENTA: 'Compra ticket',
    ABONO_RECIBIDO: 'Abono recibido',
    ABONO_PAGO: 'Abono pagado',
    PREMIO_DESCUENTO: 'Premio acreditado',
    AJUSTE_POSITIVO: 'Ajuste (+)',
    AJUSTE_NEGATIVO: 'Ajuste (-)',
    AJUSTE: 'Ajuste',
    ANULACION_CREDITO: 'Anulación',
    ANULACION_CREDITO_CLIENTE: 'Anulación',
  };
  return map[tipo] || tipo;
}

function estadoBadge(estado: string): { label: string; cls: string } {
  const map: Record<string, { label: string; cls: string }> = {
    ABIERTO: { label: 'Activo', cls: 'bg-green-800 text-green-200' },
    ANULADO: { label: 'Anulado', cls: 'bg-gray-700 text-gray-300' },
    PREMIADO: { label: 'Ganador ★', cls: 'bg-amber-700 text-amber-100' },
    CERRADO: { label: 'Cerrado', cls: 'bg-gray-700 text-gray-300' },
    LIQUIDADO: { label: 'Liquidado', cls: 'bg-gray-700 text-gray-300' },
  };
  return map[estado] || { label: estado, cls: 'bg-gray-700 text-gray-200' };
}

// ─── Ticket digital HTML para descarga ────────────────────
function generarHTMLTicket(
  ticket: CCTicket,
  cliente: CCCliente | null,
  sorteoNombreStr: string,
): string {
  const fechaHora = formatBackendDateTimeCR(ticket.created_at, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const jugadasDisplay = agruparJugadasParaDisplay(ticket.jugadas);
  let jugadasHtml = '';
  let totalDirecto = 0;
  let totalReventado = 0;

  for (const jg of jugadasDisplay) {
    if (jg.tipo === 'REVENTADO') {
      totalDirecto += jg.montoRev1;
      totalReventado += jg.montoRev2;
      jugadasHtml += `
        <tr>
          <td style="font-weight:bold;font-size:18px;">${jg.num1}</td>
          <td>${jg.montoRev1 > 0 ? `<div>Directo: ₡${jg.montoRev1.toLocaleString('es-CR')}</div>` : ''}${jg.montoRev2 > 0 ? `<div style="color:#9ca3af;">Reventado: ₡${jg.montoRev2.toLocaleString('es-CR')}</div>` : ''}</td>
          <td style="font-weight:bold;">₡${jg.monto.toLocaleString('es-CR')}</td>
        </tr>`;
    } else {
      totalDirecto += jg.monto;
      jugadasHtml += `
        <tr>
          <td style="font-weight:bold;font-size:18px;">${jg.num1}</td>
          <td>₡${jg.monto.toLocaleString('es-CR')}</td>
          <td style="font-weight:bold;">₡${jg.monto.toLocaleString('es-CR')}</td>
        </tr>`;
    }
  }

  const resumenHtml = totalReventado > 0
    ? `<tr><td>Total directo</td><td></td><td>₡${totalDirecto.toLocaleString('es-CR')}</td></tr>
       <tr><td>Total reventado</td><td></td><td>₡${totalReventado.toLocaleString('es-CR')}</td></tr>
       <tr style="font-weight:bold;font-size:16px;border-top:2px solid #374151;"><td>Total</td><td></td><td>₡${Number(ticket.total).toLocaleString('es-CR')}</td></tr>`
    : `<tr style="font-weight:bold;font-size:16px;"><td>Total</td><td></td><td>₡${Number(ticket.total).toLocaleString('es-CR')}</td></tr>`;

  const badge = ticket.estado === 'ABIERTO'
    ? '<span style="background:#d1fae5;color:#065f46;padding:2px 10px;border-radius:99px;">Activo</span>'
    : ticket.estado === 'ANULADO'
    ? '<span style="background:#f3f4f6;color:#374151;padding:2px 10px;border-radius:99px;">Anulado</span>'
    : `<span style="background:#fef3c7;color:#92400e;padding:2px 10px;border-radius:99px;">${ticket.estado}</span>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Ticket #${ticket.ticket_number || ticket.id.slice(-6)}</title>
  <style>
    body{font-family:monospace;max-width:400px;margin:0 auto;padding:20px;background:#111827;color:#f9fafb;}
    .ticket{background:#1f2937;border-radius:12px;padding:20px;border:1px solid #374151;}
    .label{color:#6b7280;font-size:11px;text-transform:uppercase;letter-spacing:1px;margin:0;}
    .value{font-size:14px;margin:0 0 12px;}
    hr{border:none;border-top:2px dashed #374151;margin:16px 0;}
    table{width:100%;border-collapse:collapse;font-size:14px;}
    th{text-align:left;padding:6px 4px;color:#6b7280;font-size:11px;border-bottom:1px solid #374151;}
    td{padding:8px 4px;border-bottom:1px solid #374151;}
    .footer{color:#4b5563;font-size:11px;text-align:center;margin-top:16px;}
  </style>
</head>
<body><div class="ticket">
  <p style="font-size:18px;font-weight:bold;margin:0 0 4px;">🎫 Ticket Digital de Crédito</p>
  <p style="color:#9ca3af;font-size:12px;margin:0 0 16px;">Tiempos V2S</p>
  <hr>
  <p class="label">Número de ticket</p>
  <p class="value" style="font-size:24px;font-weight:bold;">#${ticket.ticket_number || ticket.id.slice(-6)}</p>
  <p class="label">Estado</p><p class="value">${badge}</p>
  <p class="label">Cliente</p><p class="value">${cliente?.nombre || '—'}</p>
  <p class="label">Local</p><p class="value">${cliente?.local_nombre || cliente?.local_id || '—'}</p>
  <p class="label">Vendedor</p><p class="value">${ticket.vendedor_id || '—'}</p>
  <p class="label">Sorteo</p><p class="value">${sorteoNombreStr}</p>
  <p class="label">Cierre</p><p class="value">${formatHoraCierre(ticket.sorteo_hora_cierre)}</p>
  <p class="label">Fecha y hora (Costa Rica)</p><p class="value">${fechaHora}</p>
  <hr>
  <table><thead><tr><th>Número</th><th>Apuesta</th><th>Total</th></tr></thead><tbody>${jugadasHtml}</tbody></table>
  <hr>
  <table><tbody>${resumenHtml}</tbody></table>
  <p class="footer">Generado: ${new Date().toLocaleString('es-CR', { timeZone: 'America/Costa_Rica' })}</p>
</div></body></html>`;
}

// ─── Componente principal ──────────────────────────────────
export const CreditoClientePOS: React.FC = () => {
  const navigate = useNavigate();
  const [pestaña, setPestaña] = useState<Pestaña>('jugar');

  // Datos del cliente
  const [cliente, setCliente] = useState<CCCliente | null>(
    creditoClienteService.getStoredUser()
  );
  const [resumen, setResumen] = useState<CCResumen | null>(null);
  const [loadingResumen, setLoadingResumen] = useState(false);

  // Sorteos
  const [sorteos, setSorteos] = useState<CCSorteo[]>([]);
  const [sorteoId, setSorteoId] = useState('');

  // Jugadas pendientes en el ticket actual
  const [jugadas, setJugadas] = useState<JugadaLocal[]>([]);
  const [num1, setNum1] = useState('');

  // Montos — directo
  const [monto, setMonto] = useState<number>(200);
  const [montoPersonalizado, setMontoPersonalizado] = useState(false);
  const [montoInput, setMontoInput] = useState('');

  // Montos — reventado (REV2)
  const [montoRev2, setMontoRev2] = useState<number>(0);
  const [montoRev2Personalizado, setMontoRev2Personalizado] = useState(false);
  const [montoRev2Input, setMontoRev2Input] = useState('');

  // Tab for directo/reventado amounts
  const [amountTab, setAmountTab] = useState<'directo' | 'reventado'>('directo');


  // Estado de la operación
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [ticketsCreados, setTicketsCreados] = useState<CCTicket[]>([]);

  // Tickets y movimientos
  const [misTickets, setMisTickets] = useState<CCTicket[]>([]);
  const [movimientos, setMovimientos] = useState<CCMovimiento[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingMovs, setLoadingMovs] = useState(false);

  // Anulación
  const [anulando, setAnulando] = useState<string | null>(null);
  const [errorAnular, setErrorAnular] = useState<string | null>(null);

  // Ticket detalle/descarga
  const [verTicket, setVerTicket] = useState<CCTicket | null>(null);

  // ── Sorteo seleccionado y detección de tipo ──────────────
  const sorteoSeleccionado = sorteos.find((s) => s.id === sorteoId);
  const esReventado = sorteoSeleccionado?.tipo_id === 'REVENTADO';
  const maxDigitosNumero = maxDigitosPorSorteo(sorteoSeleccionado);
  const jugadasPorSorteo = jugadas.reduce<Record<string, JugadaLocal[]>>((acc, jugada) => {
    if (!acc[jugada.sorteo_id]) acc[jugada.sorteo_id] = [];
    acc[jugada.sorteo_id].push(jugada);
    return acc;
  }, {});

  const cambiarNumero = (valor: string) => {
    setNum1(valor.replace(/\D/g, '').slice(0, maxDigitosNumero));
  };

  // ── Limpiar jugadas al cambiar sorteo ───────────────────
  const prevSorteoId = useRef(sorteoId);
  useEffect(() => {
    if (prevSorteoId.current !== sorteoId) {
      setNum1('');
      setMonto(sorteos.find((s) => s.id === sorteoId)?.tipo_id === 'REVENTADO' ? 0 : 200);
      setMontoRev2(0);
      setMontoPersonalizado(false);
      setMontoRev2Personalizado(false);
      setAmountTab('directo');
      prevSorteoId.current = sorteoId;
    }
  }, [sorteoId, sorteos]);

  // ── Cargar datos al montar ───────────────────────────────
  const cargarResumen = useCallback(async () => {
    setLoadingResumen(true);
    try {
      const [res, me, srts] = await Promise.all([
        creditoClienteService.resumen(),
        creditoClienteService.me(),
        creditoClienteService.sorteosActivos(),
      ]);
      setResumen(res);
      setCliente(me);
      setSorteos(srts);
      if (srts.length > 0 && !sorteoId) setSorteoId(srts[0].id);
    } catch (err: any) {
      if (err.message?.includes('expirada') || err.message?.includes('autenticado')) {
        creditoClienteService.logout();
        navigate('/cliente-credito/login', { replace: true });
      }
    } finally {
      setLoadingResumen(false);
    }
  }, [sorteoId, navigate]);

  useEffect(() => {
    if (!creditoClienteService.isLoggedIn()) {
      navigate('/cliente-credito/login', { replace: true });
      return;
    }
    cargarResumen();
  }, []);

  const cargarTickets = useCallback(async () => {
    setLoadingTickets(true);
    try {
      const data = await creditoClienteService.misTickets();
      setMisTickets(data);
    } catch {
      // no bloquear
    } finally {
      setLoadingTickets(false);
    }
  }, []);

  const cargarMovimientos = useCallback(async () => {
    setLoadingMovs(true);
    try {
      const data = await creditoClienteService.misMovimientos();
      setMovimientos(data);
    } catch {
      // no bloquear
    } finally {
      setLoadingMovs(false);
    }
  }, []);

  useEffect(() => {
    if (pestaña === 'tickets') cargarTickets();
    if (pestaña === 'saldo') cargarMovimientos();
  }, [pestaña]);

  // ── Compute effective amounts ───────────────────────────
  const montoDirectoEfectivo = montoPersonalizado ? (Number(montoInput) || 0) : monto;
  const montoRevEfectivo = esReventado
    ? (montoRev2Personalizado ? (Number(montoRev2Input) || 0) : montoRev2)
    : 0;
  const totalJugadaActual = montoDirectoEfectivo + montoRevEfectivo;
  const canAddBet = num1.trim().length > 0 && totalJugadaActual > 0;

  // ── Lógica de jugadas ────────────────────────────────────
  const agregarJugada = () => {
    const montoFinal = montoDirectoEfectivo;
    const rev2Final = montoRevEfectivo;
    const numeroLimpio = num1.trim();
    const numero = numeroLimpio.padStart(2, '0');

    if (!sorteoSeleccionado) {
      setError('Seleccione un sorteo');
      return;
    }
    if (!numeroLimpio || numeroLimpio.length < 1) {
      setError('Ingrese un numero');
      return;
    }
    if (numeroLimpio.length > maxDigitosNumero) {
      setError(`Este sorteo permite maximo ${maxDigitosNumero} digitos`);
      return;
    }
    if (montoFinal < 0 || rev2Final < 0) {
      setError('El monto no puede ser negativo');
      return;
    }

    if (esReventado) {
      if (montoFinal === 0 && rev2Final === 0) {
        setError('Ingrese al menos un monto (directo o reventado)');
        return;
      }
      if (montoFinal > 0 && montoFinal < 50) {
        setError('Monto minimo ₡50');
        return;
      }
      if (rev2Final > 0 && rev2Final < 50) {
        setError('Monto reventado minimo ₡50');
        return;
      }
    } else {
      if (!montoFinal || montoFinal < 50) {
        setError('El monto minimo es ₡50');
        return;
      }
    }

    setError(null);
    setJugadas((prev) => {
      const existente = prev.find(
        (j) => j.sorteo_id === sorteoSeleccionado.id && j.num1 === numero && j.esReventado === esReventado,
      );
      if (existente) {
        return prev.map((j) => j.id === existente.id
          ? { ...j, monto: j.monto + montoFinal, montoRev2: j.montoRev2 + rev2Final }
          : j,
        );
      }
      return [
        ...prev,
        {
          id: `${Date.now()}`,
          sorteo_id: sorteoSeleccionado.id,
          sorteo_nombre: sorteoSeleccionado.nombre,
          sorteo_hora_cierre: sorteoSeleccionado.hora_cierre,
          num1: numero,
          monto: montoFinal,
          montoRev2: rev2Final,
          esReventado,
        },
      ];
    });
    // Reset for next bet
    setNum1('');
    setMonto(esReventado ? 0 : 200);
    setMontoRev2(0);
    setMontoPersonalizado(false);
    setMontoRev2Personalizado(false);
    setMontoInput('');
    setMontoRev2Input('');
  };

  const eliminarJugada = (id: string) => {
    setJugadas((prev) => prev.filter((j) => j.id !== id));
  };

  const repetirJugadas = (ticket: CCTicket) => {
    if (ticket.estado === 'ANULADO') {
      setErrorAnular('No se puede repetir un ticket anulado.');
      return;
    }

    const sorteo = sorteos.find((s) => s.id === ticket.sorteo_id && s.estado === 'ABIERTO');
    if (!sorteo) {
      setErrorAnular('No se puede repetir: el sorteo ya no esta disponible.');
      return;
    }

    const jugadasRepetidas = agruparJugadasParaDisplay(ticket.jugadas).map((jugada, index) => ({
      id: `${Date.now()}-${index}`,
      sorteo_id: sorteo.id,
      sorteo_nombre: sorteo.nombre,
      sorteo_hora_cierre: sorteo.hora_cierre,
      num1: jugada.num1,
      monto: jugada.tipo === 'REVENTADO' ? jugada.montoRev1 : jugada.monto,
      montoRev2: jugada.tipo === 'REVENTADO' ? jugada.montoRev2 : 0,
      esReventado: jugada.tipo === 'REVENTADO',
    }));

    if (jugadasRepetidas.length === 0) {
      setErrorAnular('Este ticket no tiene jugadas para repetir.');
      return;
    }

    setJugadas((prev) => {
      let acumuladas = [...prev];
      for (const repetida of jugadasRepetidas) {
        const existente = acumuladas.find(
          (j) => j.sorteo_id === repetida.sorteo_id && j.num1 === repetida.num1 && j.esReventado === repetida.esReventado,
        );
        if (existente) {
          acumuladas = acumuladas.map((j) => j.id === existente.id
            ? { ...j, monto: j.monto + repetida.monto, montoRev2: j.montoRev2 + repetida.montoRev2 }
            : j,
          );
        } else {
          acumuladas.push(repetida);
        }
      }
      return acumuladas;
    });

    setSorteoId(sorteo.id);
    setNum1('');
    setTicketsCreados([]);
    setErrorAnular(null);
    setError(null);
    setPestaña('jugar');
  };

  const totalActual = jugadas.reduce((s, j) => s + j.monto + j.montoRev2, 0);

  // ── Crear ticket ─────────────────────────────────────────
  const crearTicket = async () => {
    if (jugadas.length === 0) {
      setError('Agregue al menos una jugada');
      return;
    }
    const disponible = resumen?.disponible ? Number(resumen.disponible) : null;
    if (disponible !== null && totalActual > disponible) {
      setError('No tiene credito disponible suficiente.');
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const creados: CCTicket[] = [];
      for (const [grupoSorteoId, grupoJugadas] of Object.entries(jugadasPorSorteo)) {
        const jugadasIn: CCJugadaIn[] = [];
        for (const j of grupoJugadas) {
          if (j.esReventado) {
            if (j.monto > 0) jugadasIn.push({ num1: j.num1, num2: 'REV1', monto: j.monto });
            if (j.montoRev2 > 0) jugadasIn.push({ num1: j.num1, num2: 'REV2', monto: j.montoRev2 });
          } else {
            jugadasIn.push({ num1: j.num1, monto: j.monto });
          }
        }
        if (jugadasIn.length === 0) continue;
        const ticket = await creditoClienteService.crearTicket(grupoSorteoId, jugadasIn);
        creados.push(ticket);
      }
      setTicketsCreados(creados);
      setJugadas([]);
      cargarResumen();
    } catch (err: any) {
      setError(err.message || 'Error al crear ticket');
    } finally {
      setEnviando(false);
    }
  };

  // ── Anular ticket ─────────────────────────────────────────
  const anularTicket = async (ticketId: string) => {
    setAnulando(ticketId);
    setErrorAnular(null);
    try {
      await creditoClienteService.anularTicket(ticketId);
      await cargarTickets();
      await cargarResumen();
    } catch (err: any) {
      setErrorAnular(err.message || 'Error al anular ticket');
    } finally {
      setAnulando(null);
    }
  };

  // ── Descargar ticket ──────────────────────────────────────
  const descargarTicket = (ticket: CCTicket) => {
    const html = generarHTMLTicket(ticket, cliente, sorteoNombre(ticket, sorteos));
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ticket-credito-${ticket.ticket_number || ticket.id.slice(-6)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950 flex flex-col max-w-lg mx-auto">
      {/* Header */}
      <header className="bg-gray-900/95 backdrop-blur border-b border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-widest">Portal Credito</p>
              <p className="text-white font-bold text-sm leading-tight">{cliente?.nombre || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              {loadingResumen ? (
                <p className="text-gray-500 text-xs">Cargando...</p>
              ) : resumen ? (
                <>
                  <p className="text-xs">
                    <span className="text-gray-500">Disponible: </span>
                    <span className="text-green-400 font-bold">
                      {resumen.sin_limite ? 'Sin limite' : formatColones(resumen.disponible)}
                    </span>
                  </p>
                  <p className="text-xs">
                    <span className="text-gray-500">Pendiente: </span>
                    <span className="text-amber-400 font-bold">{formatColones(resumen.saldo_pendiente)}</span>
                  </p>
                </>
              ) : null}
            </div>
            <button
              onClick={() => {
                creditoClienteService.logout();
                navigate('/cliente-credito/login', { replace: true });
              }}
              className="text-gray-500 hover:text-red-400 text-xs flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-800 transition-colors"
            >
              <span>↪</span> Salir
            </button>
          </div>
        </div>
      </header>

      {/* Modal: Ver ticket detalle + descarga */}
      {verTicket && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end justify-center p-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
              <p className="text-white font-bold">
                🎫 Ticket #{verTicket.ticket_number || verTicket.id.slice(-6)}
              </p>
              <button onClick={() => setVerTicket(null)} className="text-gray-400 hover:text-white text-xl">
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Cliente</p>
                  <p className="text-white font-medium">{cliente?.nombre || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Estado</p>
                  <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${estadoBadge(verTicket.estado).cls}`}>
                    {estadoBadge(verTicket.estado).label}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Sorteo</p>
                  <p className="text-white text-xs">
                    {sorteoNombre(verTicket, sorteos)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Cierre</p>
                  <p className="text-white text-xs">{formatHoraCierre(sorteoHoraCierre(verTicket, sorteos))}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Vendedor</p>
                  <p className="text-white text-xs">{verTicket.vendedor_id || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Fecha</p>
                  <p className="text-white text-xs">
                    {formatBackendDateTimeCR(verTicket.created_at, {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              {verTicket.jugadas.length > 0 && (
                <div className="bg-gray-800 rounded-xl overflow-hidden">
                  <p className="text-xs text-gray-400 uppercase tracking-wide px-3 py-2 border-b border-gray-700">
                    Jugadas
                  </p>
                  <div className="divide-y divide-gray-700">
                    {agruparJugadasParaDisplay(verTicket.jugadas).map((jg, idx) => (
                      <div key={idx} className="px-3 py-2.5 flex items-start justify-between">
                        <span className="font-mono text-white text-xl font-bold">{jg.num1}</span>
                        {jg.tipo === 'REVENTADO' ? (
                          <div className="text-right text-sm">
                            {jg.montoRev1 > 0 && (
                              <p className="text-green-400">Directo: {formatColones(jg.montoRev1)}</p>
                            )}
                            {jg.montoRev2 > 0 && (
                              <p className="text-blue-400">Reventado: {formatColones(jg.montoRev2)}</p>
                            )}
                            <p className="text-white font-bold">Total: {formatColones(jg.monto)}</p>
                          </div>
                        ) : (
                          <span className="text-green-400 font-mono">{formatColones(jg.monto)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="px-3 py-2 bg-gray-700/50 flex justify-between">
                    <span className="text-gray-400 text-sm">Total</span>
                    <span className="text-white font-bold">{formatColones(verTicket.total)}</span>
                  </div>
                </div>
              )}

              <button
                onClick={() => descargarTicket(verTicket)}
                className="w-full py-3 bg-blue-700 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors"
              >
                ⬇ Descargar ticket
              </button>
              <p className="text-xs text-gray-500 text-center">
                Descarga como HTML — abrelo en el navegador para verlo o imprimirlo.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal */}
      <main className="flex-1 overflow-y-auto pb-20">
        {/* ── PESTAÑA: JUGAR ── */}
        {pestaña === 'jugar' && (
          <div className="p-3 space-y-3">
            {/* Tickets creados */}
            {ticketsCreados.length > 0 && (
              <div className="bg-green-900/60 border border-green-600 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-green-300 font-bold">
                    ✓ {ticketsCreados.length === 1 ? 'Ticket creado' : 'Tickets creados'}
                  </p>
                  <button
                    onClick={() => setTicketsCreados([])}
                    className="text-green-500 hover:text-green-300 text-lg"
                  >
                    ×
                  </button>
                </div>
                <div className="space-y-2">
                  {ticketsCreados.map((ticket) => (
                    <div key={ticket.id} className="bg-green-950/40 rounded-lg p-3 border border-green-800">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-white font-mono text-lg font-bold">
                            #{ticket.ticket_number || ticket.id.slice(-6)}
                          </p>
                          <p className="text-green-300 text-xs">{sorteoNombre(ticket, sorteos)}</p>
                        </div>
                        <p className="text-green-400 text-sm font-bold">{formatColones(ticket.total)}</p>
                      </div>
                      <div className="mt-2 space-y-1">
                        {agruparJugadasParaDisplay(ticket.jugadas).map((jg, idx) => (
                          <div key={idx} className="text-green-200 text-sm font-mono">
                            {jg.tipo === 'REVENTADO' ? (
                              <span>
                                {jg.num1} — Dir: {formatColones(jg.montoRev1)}
                                {jg.montoRev2 > 0 && ` + Rev: ${formatColones(jg.montoRev2)}`}
                              </span>
                            ) : (
                              <span>{jg.num1} — {formatColones(jg.monto)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => setVerTicket(ticket)}
                        className="w-full mt-3 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm font-bold rounded-lg"
                      >
                        Ver / Descargar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sorteo Card */}
            <SorteoCard
              sorteos={sorteos}
              sorteoId={sorteoId}
              onSorteoChange={setSorteoId}
            />

            {/* Number Grid */}
            {sorteoSeleccionado && (
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-3">
                <NumberGrid
                  value={num1}
                  maxDigits={maxDigitosNumero}
                  onChange={cambiarNumero}
                />
              </div>
            )}

            {/* Amount Section */}
            {sorteoSeleccionado && (
              <div className="bg-gray-900 rounded-2xl border border-gray-800 p-3 space-y-3">
                {/* Directo / Reventado tabs */}
                {esReventado ? (
                  <>
                    <div className="flex rounded-xl overflow-hidden border border-gray-700">
                      <button
                        type="button"
                        onClick={() => setAmountTab('directo')}
                        className={`flex-1 py-2.5 text-sm font-bold transition-all ${
                          amountTab === 'directo'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-white'
                        }`}
                      >
                        🎯 Directo
                      </button>
                      <button
                        type="button"
                        onClick={() => setAmountTab('reventado')}
                        className={`flex-1 py-2.5 text-sm font-bold transition-all ${
                          amountTab === 'reventado'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:text-white'
                        }`}
                      >
                        ✨ Reventado
                      </button>
                    </div>

                    {amountTab === 'directo' ? (
                      <AmountSelector
                        label="Monto directo"
                        value={monto}
                        isCustom={montoPersonalizado}
                        customInput={montoInput}
                        color="blue"
                        showZero={true}
                        onChange={(v, custom, input) => {
                          setMonto(v);
                          setMontoPersonalizado(custom);
                          setMontoInput(input);
                        }}
                      />
                    ) : (
                      <AmountSelector
                        label="Monto reventado (opcional)"
                        value={montoRev2}
                        isCustom={montoRev2Personalizado}
                        customInput={montoRev2Input}
                        color="blue"
                        showZero={true}
                        onChange={(v, custom, input) => {
                          setMontoRev2(v);
                          setMontoRev2Personalizado(custom);
                          setMontoRev2Input(input);
                        }}
                      />
                    )}

                    {/* Quick summary of both amounts */}
                    <div className="flex items-center justify-between text-xs bg-gray-800/50 rounded-lg px-3 py-2">
                      <span className="text-gray-400">
                        Directo: <span className="text-blue-400 font-bold">{formatColones(montoDirectoEfectivo)}</span>
                      </span>
                      <span className="text-gray-400">
                        Reventado: <span className="text-purple-400 font-bold">{formatColones(montoRevEfectivo)}</span>
                      </span>
                      <span className="text-gray-400">
                        Total: <span className="text-amber-400 font-bold">{formatColones(totalJugadaActual)}</span>
                      </span>
                    </div>
                  </>
                ) : (
                  <AmountSelector
                    label="Monto"
                    value={monto}
                    isCustom={montoPersonalizado}
                    customInput={montoInput}
                    color="blue"
                    onChange={(v, custom, input) => {
                      setMonto(v);
                      setMontoPersonalizado(custom);
                      setMontoInput(input);
                    }}
                  />
                )}
              </div>
            )}

            {/* Bet Summary + Add Button */}
            {sorteoSeleccionado && (
              <BetSummary
                numero={num1}
                montoDirec={montoDirectoEfectivo}
                montoRev={montoRevEfectivo}
                esReventado={esReventado}
                canAdd={canAddBet}
                onAdd={agregarJugada}
              />
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-900/40 border border-red-700 text-red-300 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="text-red-500 font-bold ml-2">×</button>
              </div>
            )}

            {/* Added Bets Panel */}
            <AddedBetsPanel
              jugadas={jugadas}
              totalActual={totalActual}
              enviando={enviando}
              sorteosDisponibles={sorteos.length > 0}
              onEliminar={eliminarJugada}
              onLimpiar={() => setJugadas([])}
              onCrearTicket={crearTicket}
            />
          </div>
        )}

        {/* ── PESTAÑA: MIS TICKETS ── */}
        {pestaña === 'tickets' && (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-bold text-base">Mis jugadas</h2>
              <button onClick={cargarTickets} className="text-xs text-amber-500 hover:text-amber-400">
                Actualizar
              </button>
            </div>

            {errorAnular && (
              <div className="bg-red-900/40 border border-red-700 text-red-300 rounded-lg px-4 py-3 text-sm flex items-center justify-between">
                <span>{errorAnular}</span>
                <button onClick={() => setErrorAnular(null)} className="text-red-500 font-bold ml-2">×</button>
              </div>
            )}

            {loadingTickets ? (
              <p className="text-gray-400 text-sm text-center py-8">Cargando tickets...</p>
            ) : misTickets.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">No tienes tickets registrados aun.</p>
            ) : (
              misTickets.map((t) => {
                const badge = estadoBadge(t.estado);
                const estaAnulado = t.estado === 'ANULADO';
                const estadoSorteo = t.sorteo_estado || sorteos.find((s) => s.id === t.sorteo_id)?.estado;
                const puedeAnular = t.estado === 'ABIERTO' && (!estadoSorteo || estadoSorteo === 'ABIERTO');
                const puedeRepetir = !estaAnulado && sorteos.some((s) => s.id === t.sorteo_id && s.estado === 'ABIERTO');
                return (
                  <div
                    key={t.id}
                    className={`rounded-xl border overflow-hidden ${
                      estaAnulado
                        ? 'bg-red-950/25 border-red-900/80 opacity-70 grayscale'
                        : 'bg-gray-900 border-gray-800'
                    }`}
                  >
                    <div className={`px-4 py-2 flex items-center justify-between gap-3 ${estaAnulado ? 'bg-red-950/50' : 'bg-gray-800/60'}`}>
                      <div className="min-w-0">
                        <p className={`text-xs font-bold uppercase truncate ${estaAnulado ? 'text-red-300 line-through' : 'text-amber-300'}`}>
                          {sorteoNombre(t, sorteos)}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {estaAnulado ? 'Ticket anulado' : `Cierra: ${formatHoraCierre(sorteoHoraCierre(t, sorteos))}`}
                        </p>
                      </div>
                      {estaAnulado ? (
                        <p className="text-[10px] font-black text-red-200 bg-red-900/80 px-2 py-1 rounded uppercase tracking-wide">
                          Anulado
                        </p>
                      ) : (
                        <p className={`text-[10px] font-bold ${estadoSorteo === 'ABIERTO' || !estadoSorteo ? 'text-green-400' : 'text-red-400'}`}>
                          {estadoSorteo === 'ABIERTO' || !estadoSorteo ? 'Abierto' : 'Cerrado'}
                        </p>
                      )}
                    </div>
                    <div className="px-4 py-3 flex items-start justify-between">
                      <div>
                        <p className={`font-mono font-bold text-lg ${estaAnulado ? 'text-red-200 line-through' : 'text-white'}`}>
                          #{t.ticket_number || t.id.slice(-6)}
                        </p>
                        <p className="text-gray-400 text-xs mt-0.5">
                          {formatBackendDateTimeCR(t.created_at, {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>
                          {badge.label}
                        </span>
                        <p className={`font-mono text-base font-bold mt-1 ${estaAnulado ? 'text-red-300 line-through' : 'text-green-400'}`}>
                          {formatColones(t.total)}
                        </p>
                      </div>
                    </div>

                    {estaAnulado && (
                      <div className="mx-4 mb-3 rounded-lg border border-red-900/70 bg-red-950/40 px-3 py-2 text-center">
                        <p className="text-red-200 text-xs font-black uppercase tracking-wide">
                          Ticket anulado - sin validez
                        </p>
                      </div>
                    )}

                    {/* Jugadas con soporte REVENTADO */}
                    {t.jugadas.length > 0 && (
                      <div className={`border-t px-4 py-2 space-y-1.5 ${estaAnulado ? 'border-red-950/70' : 'border-gray-800'}`}>
                        {agruparJugadasParaDisplay(t.jugadas).map((jg, idx) => (
                          <div key={idx} className="flex justify-between items-start text-sm">
                            <span className={`font-mono font-bold ${estaAnulado ? 'text-red-200 line-through' : 'text-white'}`}>{jg.num1}</span>
                            {jg.tipo === 'REVENTADO' ? (
                              <div className="text-right text-xs">
                                {jg.montoRev1 > 0 && (
                                  <span className={`mr-2 ${estaAnulado ? 'text-red-300 line-through' : 'text-green-400'}`}>Dir: {formatColones(jg.montoRev1)}</span>
                                )}
                                {jg.montoRev2 > 0 && (
                                  <span className={estaAnulado ? 'text-red-300 line-through' : 'text-blue-400'}>Rev: {formatColones(jg.montoRev2)}</span>
                                )}
                              </div>
                            ) : (
                              <span className={estaAnulado ? 'text-red-300 line-through' : 'text-gray-400'}>{formatColones(jg.monto)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Acciones */}
                    <div className={`border-t px-4 py-2 ${estaAnulado ? 'border-red-950/70' : 'border-gray-800'}`}>
                      {estaAnulado ? (
                        <div className="py-3 text-xs text-red-200 rounded-lg text-center font-bold bg-red-950/50 border border-red-900/70 uppercase tracking-wide">
                          No disponible para ver ni descargar
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {puedeRepetir ? (
                            <button
                              onClick={() => repetirJugadas(t)}
                              className="py-2 text-xs font-bold bg-amber-500 hover:bg-amber-400 text-black rounded-lg"
                            >
                              Repetir
                            </button>
                          ) : (
                            <div className="py-2 text-xs text-gray-600 rounded-lg text-center">
                              No repetir
                            </div>
                          )}
                          <button
                            onClick={() => setVerTicket(t)}
                            className="py-2 text-xs font-bold bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg"
                          >
                            Ver
                          </button>
                          <button
                            onClick={() => descargarTicket(t)}
                            className="py-2 text-xs font-bold bg-blue-900/70 hover:bg-blue-900 text-blue-200 rounded-lg"
                          >
                            Descargar
                          </button>
                          {puedeAnular ? (
                            <button
                              onClick={() => anularTicket(t.id)}
                              disabled={anulando === t.id}
                              className="py-2 text-xs font-bold bg-red-900/60 hover:bg-red-900 disabled:opacity-50 text-red-300 rounded-lg border border-red-800"
                            >
                              {anulando === t.id ? 'Anulando...' : '✕ Eliminar'}
                            </button>
                          ) : (
                            <div className="py-2 text-xs text-gray-600 rounded-lg text-center">
                              No eliminar
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── PESTAÑA: SALDO / MOVIMIENTOS ── */}
        {pestaña === 'saldo' && (
          <div className="p-4 space-y-4">
            {resumen && (
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-4 space-y-3">
                <h2 className="text-white font-bold">Mi saldo</h2>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-400">{Number(resumen.saldo_pendiente) < 0 ? 'Saldo a favor' : 'Pendiente'}</p>
                    <p className={`${Number(resumen.saldo_pendiente) < 0 ? 'text-green-400' : 'text-amber-400'} font-bold text-xl`}>
                      {formatColones(Math.abs(Number(resumen.saldo_pendiente)))}
                    </p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-400">Disponible</p>
                    <p className="text-green-400 font-bold text-xl">
                      {resumen.sin_limite ? 'Sin limite' : formatColones(resumen.disponible)}
                    </p>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3 col-span-2">
                    <p className="text-xs text-gray-400">Limite asignado</p>
                    <p className="text-gray-200 font-bold text-lg">
                      {resumen.sin_limite ? 'Sin limite' : formatColones(resumen.limite_credito)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-sm">Movimientos</h3>
              <button onClick={cargarMovimientos} className="text-xs text-amber-500 hover:text-amber-400">
                Actualizar
              </button>
            </div>
            {loadingMovs ? (
              <p className="text-gray-400 text-sm text-center py-6">Cargando...</p>
            ) : movimientos.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-6">Sin movimientos aun.</p>
            ) : (
              <div className="space-y-2">
                {movimientos.map((m) => {
                  const esCargo = m.efecto === 'SUMA';
                  const esPositivo = m.efecto === 'RESTA';
                  return (
                    <div
                      key={m.id}
                      className="bg-gray-900 rounded-xl border border-gray-800 px-4 py-3 flex items-center justify-between"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeTipo(m.tipo)}`}>
                            {labelTipo(m.tipo, m.descripcion)}
                          </span>
                        </div>
                        <p className="text-gray-500 text-xs mt-1">
                          {formatBackendDateTimeCR(m.created_at, {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                          })}
                        </p>
                        {m.descripcion && (
                          <p className="text-gray-500 text-xs truncate max-w-[200px]">{m.descripcion}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-mono font-bold text-base ${esCargo ? 'text-red-400' : esPositivo ? 'text-green-400' : 'text-gray-300'}`}>
                          {esCargo ? '-' : '+'}
                          {formatColones(m.monto)}
                        </p>
                        {m.saldo_nuevo !== null && m.saldo_nuevo !== undefined && (
                          <p className="text-gray-600 text-xs">Saldo: {formatColones(m.saldo_nuevo)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer nav */}
      <nav className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-gray-900/95 backdrop-blur border-t border-gray-800 flex safe-area-bottom">
        {(
          [
            { id: 'jugar', label: 'Jugar', icon: '🎯' },
            { id: 'tickets', label: 'Tickets', icon: '🎫' },
            { id: 'saldo', label: 'Saldo', icon: '💰' },
          ] as { id: Pestaña; label: string; icon: string }[]
        ).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setPestaña(tab.id)}
            className={`flex-1 flex flex-col items-center py-3 text-xs font-medium transition-colors ${
              pestaña === tab.id
                ? 'text-blue-400 border-t-2 border-blue-400 -mt-px'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <span className="text-xl mb-0.5">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
};
